// @ts-nocheck
import * as React from "react";
import * as V from "../api";
import {
  Badge, Button, Card, Empty, Field, Input, Loading, Modal, Select, TOKENS, Textarea,
  errorMessage, useToast,
} from "../ui";

// Phase 4 — 권역별 토의(土宜) 교육과정(아젠다 ③). draft → published → archived.

const CURR_FLOW: Record<string, string[]> = {
  draft:     ["published"],
  published: ["archived"],
  archived:  [],
};

export function CurriculaPanel({ bureaus, isOperator }: { bureaus: V.Bureau[]; isOperator: boolean }) {
  const toast = useToast();
  const [status, setStatus] = React.useState("");
  const [list, setList] = React.useState<V.Curriculum[] | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [detail, setDetail] = React.useState<V.Curriculum | null>(null);

  const reload = React.useCallback(() => {
    V.curricula.list({ status: status || undefined })
      .then((r) => setList(r.items))
      .catch((e) => { setList([]); toast.push(errorMessage(e), "danger"); });
  }, [status, toast]);
  React.useEffect(() => { reload(); }, [reload]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>토의 교육과정</h2>
        <div style={{ display: "flex", gap: 6 }}>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 120 }}>
            <option value="">전체</option>
            {Object.entries(V.LABELS.curriculumStatus).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
          {isOperator && <Button onClick={() => setCreating(true)}>커리큘럼 작성</Button>}
        </div>
      </div>

      {list === null ? <Loading /> :
       list.length === 0 ? <Empty title="조회 가능한 교육과정이 없습니다." hint={isOperator ? undefined : "비운영자는 발행(published)된 것만 볼 수 있습니다."} /> :
        <div style={{ display: "grid", gap: 8 }}>
          {list.map((c) => (
            <Card key={c.id} onClick={() => setDetail(c)}>
              <div style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {c.title}
                <Badge tone={c.status === "published" ? "success" : c.status === "draft" ? "warning" : "neutral"}>
                  {V.LABELS.curriculumStatus[c.status]}
                </Badge>
                {c.bureau && <Badge tone="info">{c.bureau.name}</Badge>}
              </div>
              <div style={{ fontSize: 12, color: TOKENS.ink3, marginTop: 4 }}>
                {c.tags.length > 0 && `#${c.tags.join(" #")} · `}
                {c.session_count ? `${c.session_count}차시 · ` : ""}
                학년 {gradeRange(c.min_grade, c.max_grade)}
              </div>
              {c.summary && (
                <div style={{ fontSize: 12, color: TOKENS.ink2, marginTop: 6, lineHeight: 1.6 }}>
                  {c.summary}
                </div>
              )}
            </Card>
          ))}
        </div>
      }

      <CurriculumCreateModal
        open={creating}
        bureaus={bureaus}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); reload(); }}
      />

      <CurriculumDetail
        open={!!detail}
        curriculum={detail}
        isOperator={isOperator}
        onClose={() => setDetail(null)}
        onChanged={() => { setDetail(null); reload(); }}
      />
    </div>
  );
}

function CurriculumCreateModal({ open, bureaus, onClose, onCreated }: {
  open: boolean; bureaus: V.Bureau[]; onClose: () => void; onCreated: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = React.useState("");
  const [bureauCode, setBureauCode] = React.useState<V.BureauCode>("L");
  const [summary, setSummary] = React.useState("");
  const [outline, setOutline] = React.useState("");
  const [tags, setTags] = React.useState("");
  const [minGrade, setMinGrade] = React.useState("");
  const [maxGrade, setMaxGrade] = React.useState("");
  const [sessionCount, setSessionCount] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTitle(""); setBureauCode("L"); setSummary(""); setOutline("");
    setTags(""); setMinGrade(""); setMaxGrade(""); setSessionCount("");
    setSubmitting(false);
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await V.curricula.create({
        title: title.trim(),
        bureau_code: bureauCode,
        summary: summary || undefined,
        outline: outline || undefined,
        tags: tags || undefined,
        min_grade: minGrade ? Number(minGrade) : undefined,
        max_grade: maxGrade ? Number(maxGrade) : undefined,
        session_count: sessionCount ? Number(sessionCount) : undefined,
      });
      toast.push("커리큘럼을 초안으로 저장했습니다.", "success");
      onCreated();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const activeBureaus = bureaus.filter((b) => b.kind === "bureau");

  return (
    <Modal
      open={open}
      title="토의 교육과정 작성"
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>취소</Button>
          <Button onClick={submit} disabled={submitting || !title.trim()}>
            {submitting ? "저장 중…" : "초안 저장"}
          </Button>
        </>
      }
    >
      <Field label="제목" required>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="담당 교육국">
          <Select value={bureauCode} onChange={(e) => setBureauCode(e.target.value as V.BureauCode)}>
            {activeBureaus.map((b) => (
              <option key={b.code} value={b.code}>{b.name}</option>
            ))}
          </Select>
        </Field>
        <Field label="차시 수">
          <Input type="number" min={1} value={sessionCount} onChange={(e) => setSessionCount(e.target.value)} />
        </Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="최소 학년">
          <Input type="number" min={1} max={12} value={minGrade} onChange={(e) => setMinGrade(e.target.value)} />
        </Field>
        <Field label="최대 학년">
          <Input type="number" min={1} max={12} value={maxGrade} onChange={(e) => setMaxGrade(e.target.value)} />
        </Field>
      </div>
      <Field label="태그" hint="쉼표로 구분. 예: 생태, 환경, 토의">
        <Input value={tags} onChange={(e) => setTags(e.target.value)} />
      </Field>
      <Field label="요약">
        <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} />
      </Field>
      <Field label="차시 개요 (Markdown)">
        <Textarea value={outline} onChange={(e) => setOutline(e.target.value)}
                  style={{ minHeight: 120 }} placeholder="## 1차시&#10;주제·활동" />
      </Field>
    </Modal>
  );
}

function CurriculumDetail({ open, curriculum, isOperator, onClose, onChanged }: {
  open: boolean; curriculum: V.Curriculum | null; isOperator: boolean;
  onClose: () => void; onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);
  if (!curriculum) return null;

  const nextStates = CURR_FLOW[curriculum.status] || [];
  const transition = async (to: string) => {
    setBusy(true);
    try {
      await V.curricula.transition(curriculum.id, to);
      toast.push(`${V.LABELS.curriculumStatus[to]} 상태로 전이했습니다.`, "success");
      onChanged();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally { setBusy(false); }
  };

  return (
    <Modal
      open={open}
      title={curriculum.title}
      onClose={onClose}
      width={680}
      footer={
        <>
          {isOperator && nextStates.length > 0 && (
            <div style={{ display: "flex", gap: 6 }}>
              {nextStates.map((to) => (
                <Button key={to} variant="secondary" size="sm" disabled={busy} onClick={() => transition(to)}>
                  → {V.LABELS.curriculumStatus[to]}
                </Button>
              ))}
            </div>
          )}
          <div style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>닫기</Button>
        </>
      }
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <Badge tone={curriculum.status === "published" ? "success" : curriculum.status === "draft" ? "warning" : "neutral"}>
          {V.LABELS.curriculumStatus[curriculum.status]}
        </Badge>
        {curriculum.bureau && <Badge tone="info">{curriculum.bureau.name}</Badge>}
        {curriculum.tags.map((t) => <Badge key={t} tone="neutral">#{t}</Badge>)}
      </div>
      <div style={{ fontSize: 12, color: TOKENS.ink3, marginBottom: 10 }}>
        {curriculum.session_count ? `${curriculum.session_count}차시 · ` : ""}
        학년 {gradeRange(curriculum.min_grade, curriculum.max_grade)}
        {curriculum.published_at && ` · 발행 ${fmt(curriculum.published_at)}`}
      </div>
      {curriculum.summary && (
        <p style={{ fontSize: 13, lineHeight: 1.7, color: TOKENS.ink2 }}>{curriculum.summary}</p>
      )}
      {curriculum.outline && (
        <pre style={{
          marginTop: 12, padding: 12, background: TOKENS.bgSub, borderRadius: 10,
          fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap",
          fontFamily: "var(--font-mono, monospace)", color: TOKENS.ink2,
        }}>{curriculum.outline}</pre>
      )}
    </Modal>
  );
}

function gradeRange(min: number | null, max: number | null) {
  if (min == null && max == null) return "제한 없음";
  if (min == null) return `~${max}학년`;
  if (max == null) return `${min}학년~`;
  return `${min}~${max}학년`;
}
function fmt(iso: string) { try { return new Date(iso).toLocaleDateString("ko-KR"); } catch { return iso; } }
